import React, { useState } from "react";
import { Button } from "@heroui/react";
import { Icon } from "@iconify/react";

const CAMERA_TIMEOUT_MS = 3000;

// Lado maior da foto. Acima disso o JPEG (que ainda é convertido em base64 no
// envio, em `new-message`/`messages`) pesa demais para a WebView sem ganho de
// legibilidade em comprovante/documento.
const MAX_PHOTO_EDGE = 3840;
const JPEG_QUALITY = 0.92;
const APPLY_CONSTRAINTS_TIMEOUT_MS = 2500;

function stopStream(stream: MediaStream | null | undefined) {
  stream?.getTracks().forEach((track) => track.stop());
}

/**
 * Sobe a resolução **depois** que o stream já está vivo, em vez de pedir
 * `width/height` no `getUserMedia`.
 *
 * Sem nenhuma constraint o Chrome/WebView do Android entrega o formato padrão
 * de **640x480** — daí o anexo sair pixelado. Mas pedir 4K já no `getUserMedia`
 * deixa a abertura da câmera mais lenta e faria a corrida com o
 * `CAMERA_TIMEOUT_MS` (que existe para o caso do WebView sem permissão, onde a
 * promise nunca resolve) disparar falso negativo em aparelho lento. Trocar o
 * formato do track vivo não passa por essa corrida.
 *
 * `focusMode: "continuous"` é o que ataca o desfoque em si: o frame de preview
 * não trava o autofoco como o pipeline de still faz.
 */
async function upgradeVideoTrack(track: MediaStreamTrack) {
  const caps = track.getCapabilities?.() as
    | (MediaTrackCapabilities & { focusMode?: string[] })
    | undefined;
  if (!caps) return;

  const constraints: MediaTrackConstraints & { advanced?: unknown[] } = {};

  const maxWidth = caps.width?.max;
  const maxHeight = caps.height?.max;
  if (maxWidth && maxHeight) {
    // Mantém o aspecto do sensor ao aplicar o teto.
    const scale = Math.min(1, MAX_PHOTO_EDGE / Math.max(maxWidth, maxHeight));
    constraints.width = { ideal: Math.round(maxWidth * scale) };
    constraints.height = { ideal: Math.round(maxHeight * scale) };
  }
  if (caps.focusMode?.includes("continuous")) {
    constraints.advanced = [{ focusMode: "continuous" }];
  }

  if (!constraints.width && !constraints.advanced) return;
  try {
    // A troca de formato é aguardada antes de liberar o obturador (evita
    // capturar no meio da renegociação), então precisa de teto: no WebView um
    // `applyConstraints` pendurado deixaria o overlay "Abrindo câmera..." para
    // sempre. Estourando o prazo, segue com o formato que estiver valendo.
    await Promise.race([
      track.applyConstraints(constraints),
      new Promise((resolve) => setTimeout(resolve, APPLY_CONSTRAINTS_TIMEOUT_MS)),
    ]);
  } catch {
    // Aparelho que não aceita o formato segue no padrão dele — a captura via
    // ImageCapture ainda entrega a resolução de foto.
  }
}

/**
 * Captura pelo pipeline de **still** do dispositivo (mesma coisa que o app de
 * câmera do sistema faz): resolução de foto, com foco e exposição travados.
 * O canvas só consegue copiar o frame de *preview*, que é menor e mole.
 *
 * Devolve `null` quando o aparelho não tem `ImageCapture`, quando o
 * `takePhoto()` falha (acontece em alguns Androids) ou quando o formato não é
 * JPEG — nesses casos o chamador cai no canvas.
 */
async function captureStillPhoto(track: MediaStreamTrack): Promise<Blob | null> {
  const ImageCaptureCtor = (window as any).ImageCapture;
  if (!ImageCaptureCtor) return null;

  try {
    const imageCapture = new ImageCaptureCtor(track);

    let settings: Record<string, number> | undefined;
    try {
      const photoCaps = await imageCapture.getPhotoCapabilities?.();
      const maxWidth = photoCaps?.imageWidth?.max;
      const maxHeight = photoCaps?.imageHeight?.max;
      if (maxWidth && maxHeight) {
        const scale = Math.min(1, MAX_PHOTO_EDGE / Math.max(maxWidth, maxHeight));
        settings = {
          imageWidth: Math.round(maxWidth * scale),
          imageHeight: Math.round(maxHeight * scale),
        };
      }
    } catch {
      // Sem as capabilities, tira na configuração padrão do aparelho.
    }

    let blob: Blob | undefined;
    try {
      blob = await imageCapture.takePhoto(settings);
    } catch {
      // Chromium recusa combinação de tamanho não suportada pelo sensor.
      blob = await imageCapture.takePhoto();
    }

    if (!blob?.size || blob.type !== "image/jpeg") return null;
    return blob;
  } catch {
    return null;
  }
}

function captureFromVideo(
  video: HTMLVideoElement,
  canvas: HTMLCanvasElement
): Promise<Blob | null> {
  // `videoWidth` é a resolução do track (não a da tela) — usar o tamanho
  // renderizado encolheria a foto de novo.
  const { videoWidth, videoHeight } = video;
  const ctx = canvas.getContext("2d");
  if (!videoWidth || !videoHeight || !ctx) return Promise.resolve(null);

  canvas.width = videoWidth;
  canvas.height = videoHeight;
  ctx.drawImage(video, 0, 0, videoWidth, videoHeight);
  return new Promise((resolve) =>
    canvas.toBlob((blob) => resolve(blob), "image/jpeg", JPEG_QUALITY)
  );
}

interface CameraFullscreenProps {
  open: boolean;
  onClose: () => void;
  onCapture: (
    image:
      | File
      | {
          name: string;
          originalName: string;
          uri: string;
          type: string;
          editing: boolean;
        }
  ) => void;
}

export function CameraFullscreen({
  open,
  onClose,
  onCapture,
}: CameraFullscreenProps) {
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const streamRef = React.useRef<MediaStream | null>(null);
  const openRef = React.useRef(open);
  const [error, setError] = useState("");
  const [checking, setChecking] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [captureError, setCaptureError] = useState("");
  const [captured, setCaptured] = useState<File | null>(null);
  const [photoName, setPhotoName] = useState("foto.jpg");

  React.useEffect(() => {
    if (!open || captured || !videoRef.current) return;

    setError("");
    setCaptureError("");
    let cancelled = false;
    let timedOut = false;
    setChecking(true);

    const request = navigator.mediaDevices.getUserMedia({
      video: { facingMode: "environment" },
    });
    // Se o timeout vencer a corrida, o `getUserMedia` ainda resolve depois: sem
    // parar esse stream a câmera fica ligada e a abertura seguinte pega o
    // dispositivo ocupado.
    request
      .then((s) => {
        if (cancelled || timedOut) stopStream(s);
      })
      .catch(() => {});

    Promise.race([
      request,
      new Promise<MediaStream>((_, reject) =>
        setTimeout(() => {
          timedOut = true;
          reject(new Error("timeout"));
        }, CAMERA_TIMEOUT_MS)
      ),
    ])
      .then(async (s) => {
        if (cancelled || !videoRef.current) {
          stopStream(s);
          return;
        }
        streamRef.current = s;
        videoRef.current.srcObject = s;
        videoRef.current.play();

        const track = s.getVideoTracks()[0];
        if (track) await upgradeVideoTrack(track);
      })
      .catch(() => {
        if (!cancelled) {
          setError("Não foi possível acessar a câmera. Verifique a permissão do aplicativo.");
        }
      })
      .finally(() => {
        if (!cancelled) setChecking(false);
      });

    return () => {
      cancelled = true;
      stopStream(streamRef.current);
      streamRef.current = null;
      // Parar os tracks não desassocia o stream do elemento: sem isso o <video>
      // continua segurando o stream morto (e o último frame) até que um novo
      // srcObject seja atribuído.
      if (videoRef.current) videoRef.current.srcObject = null;
    };
  }, [open, captured]);

  // Os callers mantêm o componente montado e só alternam `open`, então o estado
  // sobrevive entre aberturas e precisa ser limpo.
  //
  // O reset é na **abertura**, não no fechamento: `handleCapture` é assíncrono
  // (`takePhoto` leva 1-2s no Android) e o X de fechar segue ativo enquanto o
  // obturador está desabilitado. Fechando no meio da captura, o `setCaptured`
  // resolve *depois* do fechamento — um reset ao fechar rodaria antes dele e a
  // foto ressurgiria como preview na abertura seguinte, travando a câmera (o
  // efeito acima tem `captured` no guard). Resetar ao abrir garante o invariante
  // "abrir sempre começa na câmera ao vivo" sem depender dessa ordem.
  React.useEffect(() => {
    openRef.current = open;
    setCaptured(null);
    setPhotoName("foto.jpg");
    setCaptureError("");
    setError("");
  }, [open]);

  // Sem o memo, cada render criava uma object URL nova e nenhuma era revogada —
  // com a foto em resolução cheia isso segura vários MB por render.
  const capturedUrl = React.useMemo(
    () => (captured ? URL.createObjectURL(captured) : ""),
    [captured]
  );
  React.useEffect(() => {
    if (!capturedUrl) return;
    return () => URL.revokeObjectURL(capturedUrl);
  }, [capturedUrl]);

  const handleCapture = async () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || capturing) return;

    setCaptureError("");
    setCapturing(true);
    try {
      const track = streamRef.current?.getVideoTracks()[0];
      // Still primeiro (resolução de foto, foco travado); canvas é o fallback.
      const blob =
        (track ? await captureStillPhoto(track) : null) ??
        (await captureFromVideo(video, canvas));

      if (!blob) {
        setCaptureError("Não foi possível tirar a foto. Tente novamente.");
        return;
      }
      // Fechou durante a captura: descarta em vez de gravar estado num
      // componente fechado (ver o reset na abertura).
      if (!openRef.current) return;
      setCaptured(new File([blob], photoName, { type: "image/jpeg" }));
    } finally {
      setCapturing(false);
    }
  };

  const saveCapture = () => {
    if (!captured) return;
    const fileUrl = URL.createObjectURL(captured);
    onCapture({
      name: photoName,
      originalName: photoName,
      uri: fileUrl,
      type: "image/jpeg",
      editing: false,
    });
    onClose();
  };

  const handleRetake = () => {
    setCaptured(null);
    setPhotoName("foto.jpg");
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col items-center justify-center">
      {/* Top bar */}
      <div className="absolute top-0 left-0 w-full flex justify-between items-center p-4 z-10">
        <button
          onClick={onClose}
          className="text-white bg-black/50 rounded-full p-2"
        >
          <Icon icon="lucide:x" className="text-2xl" />
        </button>
        <span />
      </div>
      {/* Camera or Preview */}
      <div className="flex-1 w-full flex flex-col items-center justify-center">
        {!captured ? (
          <>
            {error ? (
              <span className="text-danger text-sm mt-8 px-8 text-center">
                {error}
              </span>
            ) : (
              <>
                <video
                  ref={videoRef}
                  className="w-full h-full object-cover"
                  autoPlay
                  playsInline
                  muted
                />
                {checking && (
                  <span className="absolute inset-0 flex items-center justify-center text-white text-sm">
                    Abrindo câmera...
                  </span>
                )}
              </>
            )}
            <canvas ref={canvasRef} className="hidden" />
            {captureError && (
              <span className="absolute bottom-36 left-1/2 -translate-x-1/2 w-4/5 text-center text-white text-sm bg-black/70 rounded px-3 py-2">
                {captureError}
              </span>
            )}
            {!error && !checking && (
              <button
                className="absolute bottom-12 left-1/2 -translate-x-1/2 w-20 h-20 rounded-full border-4 border-white bg-white/80 flex items-center justify-center shadow-lg active:scale-95 transition disabled:opacity-50"
                onClick={handleCapture}
                disabled={capturing}
                aria-label="Capturar foto"
              >
                <span className="block w-14 h-14 rounded-full bg-primary-500" />
              </button>
            )}
          </>
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-end">
            <img
              src={capturedUrl}
              alt="Foto capturada"
              className="w-full h-full object-cover absolute top-0 left-0"
            />
            <div className="relative z-10 w-full bg-black/70 px-4 pb-8 pt-4 flex flex-col items-center">
              <input
                className="mb-4 w-full px-3 py-2 rounded border border-default-200 focus:outline-none focus:ring-2 focus:ring-primary-500 text-center"
                value={photoName}
                onChange={(e) => setPhotoName(e.target.value)}
                placeholder="Nome da foto"
              />
              <div className="flex gap-2 w-full">
                <Button
                  variant="flat"
                  className="flex-1"
                  onPress={handleRetake}
                >
                  Refazer
                </Button>
                <Button
                  color="primary"
                  className="flex-1"
                  onPress={saveCapture}
                >
                  Salvar
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
